import type { ReactNode } from 'react';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteCommandType } from '@gitbook/react-uikit';

import { apiResource, readAPICache } from '../../state';
import { flattenSpacesInOrgAllContent } from '../Organization';
import { SpaceIcon } from '../Space';

export type SelectSpacePaletteItem = {
    key: string;
    space: api.Space;
    title: string;
    icon: ReactNode;
};

/**
 * Get a palette item for a space.
 */
export function getSpacePaletteItem(space: api.Space) {
    return {
        key: space.id,
        title: space.title,
        icon: <SpaceIcon space={space} />,
    };
}

/**
 * Create a palette for selecting a space from already-resolved display items.
 */
export function createSelectSpacePalette(props: {
    items: SelectSpacePaletteItem[];
    selectedSpaceId?: string;
    onSpaceSelected: (space: api.Space) => void;
    searchPlaceholder?: string;
}) {
    const { items, selectedSpaceId, onSpaceSelected, searchPlaceholder = 'Find space…' } = props;

    return createPalette({
        search: { placeholder: searchPlaceholder },
        groups: [
            {
                key: 'spaces',
                commands: items.map((item) => {
                    const { key, space } = item;
                    const baseCommand = {
                        key,
                        title: item.title,
                        icon: item.icon,
                        trigger: async () => onSpaceSelected(space),
                    };

                    if (selectedSpaceId) {
                        return {
                            ...baseCommand,
                            type: PaletteCommandType.RADIO as const,
                            selected: selectedSpaceId === space.id,
                        };
                    }

                    return baseCommand;
                }),
            },
        ],
    });
}

/**
 * Simple hook for creating a palette to select a space.
 */
export function useSelectSpacePalette(props: {
    selectedSpaceId?: string;
    onSpaceSelected: (space: api.Space) => void;
    organizationId: string;
    filterSpace?: (space: api.Space) => boolean;
}) {
    const { selectedSpaceId, onSpaceSelected, organizationId, filterSpace = () => true } = props;

    return () => {
        const allContent = readAPICache(
            apiResource('getOrganizationAllContent', { organizationId }),
            { staleWhileRevalidate: true }
        );
        const items = flattenSpacesInOrgAllContent(allContent, ({ space }) => {
            if (!filterSpace(space)) {
                return null;
            }
            return {
                space,
                ...getSpacePaletteItem(space),
            };
        });

        return createSelectSpacePalette({
            items,
            selectedSpaceId,
            onSpaceSelected,
        });
    };
}
